import { useZionFormContext } from '@fs/zion-form'
import { ContentAdd, NoticeWarning } from '@fs/zion-icon'
import { BillboardButton, Button, DialogOverlay, DialogOverlayActions, colors } from '@fs/zion-ui'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cloneDeep from 'lodash.clonedeep'
import { useSlsStateContext } from '@fs/zion-sls'
import useTreatedFieldChips from '../../../../hooks/useTreatedFieldChips'
import FieldChipSelector from '../shared/fieldChipSelector/FieldChipSelector'
import { createFieldValueFromChip, EVENT_FIELD_TYPES as eventFieldTypes, removeEventField } from './utils/eventUtils'
import { translateEventFieldChips } from '../../../../utils/translationUtils'

const EventFieldsSelector = ({ overlay, title, disabledFunc, unlinkingStates, fieldTypeKey }) => {
  const { types } = useSlsStateContext()
  const [t] = useTranslation()
  const formMethods = useZionFormContext()
  const { getValues, setValue, trigger } = formMethods
  const [formLockedChips, formSelectedChips] = getValues(['lockedChips', 'selectedChips'])
  const [currentChips, setCurrentChips] = useState(formSelectedChips)
  const [currentLockedChips, setCurrentLockedChips] = useState(formLockedChips)
  const fieldType = eventFieldTypes[fieldTypeKey]
  const fieldTypeName = useMemo(() => fieldTypeKey.toLowerCase(), [fieldTypeKey])

  const { disabled, message } = disabledFunc ? disabledFunc(currentChips.concat(currentLockedChips), t) : {}
  const formValues = getValues()
  const selectedFields = formValues[fieldType] || []

  const { treatedFieldChipData } = useTreatedFieldChips(
    types,
    selectedFields,
    currentChips,
    currentLockedChips,
    translateEventFieldChips
  )

  const filteredFieldChipData = useMemo(
    () =>
      fieldTypeKey
        ? treatedFieldChipData.filter((chip) => chip.elementTypes.includes(fieldTypeKey))
        : treatedFieldChipData,
    [treatedFieldChipData, fieldTypeKey]
  )

  const handleChipSelection = useCallback(
    (chips, lockedChips) => {
      const currentFormValues = getValues()
      const removedInitialValues = [...currentFormValues.removedFields]

      let removedFieldsCount = 0
      const currentFieldsArray = cloneDeep(currentFormValues[fieldType]) || []
      const selectedChipsIds = chips.map(({ id }) => id)
      const lockedChipsIds = lockedChips.map(({ id }) => id)
      currentFieldsArray
        .filter((field) => !selectedChipsIds.includes(field.id) && !lockedChipsIds.includes(field.id))
        .forEach((field) => {
          const index = currentFieldsArray.findIndex((f) => f.id === field.id)
          if (index !== -1) {
            removeEventField({ form: formMethods, removedFieldsArray: removedInitialValues, field, fieldTypeKey })
            currentFieldsArray.splice(index, 1)
            removedFieldsCount++
          }
        })

      const newChips = chips.filter((chip) => {
        if (chip.elementTypes?.includes(fieldTypeKey)) {
          return !chip.id
        }
        if (fieldTypeKey === 'EVENT' && !chip.elementTypes?.includes('PLACE') && !chip.elementTypes?.includes('DATE')) {
          return !chip.id
        }

        return false
      })

      if (newChips) {
        newChips.reverse()
        newChips.forEach((chip) => {
          const newField = createFieldValueFromChip({ formValues: currentFormValues, chip, superType: fieldTypeKey })
          currentFieldsArray.push(newField)
        })
      }

      setValue(fieldType, currentFieldsArray, { shouldDirty: true })

      if (removedFieldsCount > 0) {
        trigger() // Revalidate if fields are removed
      }
    },
    [getValues, setValue, trigger, fieldType, fieldTypeKey, formMethods]
  )

  const saveButtonHandler = useCallback(() => {
    handleChipSelection(currentChips, currentLockedChips)
    overlay.close()
  }, [overlay, handleChipSelection, currentChips, currentLockedChips])

  return (
    <>
      <BillboardButton
        data-testid={`add-or-remove-${fieldTypeName}-fields`}
        Icon={ContentAdd}
        color="yellow"
        emphasis="low"
        onClick={overlay.handleClick}
        linkName={`Edit event add or remove ${fieldTypeName} fields`}
      >
        {t('edit-index.add-fields.button', 'Add or Remove Fields')}
      </BillboardButton>
      <DialogOverlay
        {...overlay}
        title={title}
        footer={
          <DialogOverlayActions>
            <Button
              data-testid={`${fieldTypeName}-fields-selector-cancel-save-fields`}
              linkName={`${fieldTypeName} fields selector cancel`}
              onClick={() => overlay.close()}
            >
              {t('common-ui:cancel.action', 'Cancel')}
            </Button>
            <Button
              data-testid={`${fieldTypeName}-fields-selector-save-fields`}
              linkName={`${fieldTypeName} fields selector save`}
              onClick={saveButtonHandler}
              emphasis="high"
              disabled={disabled}
            >
              {t('field-selector-overlay.save.button', 'Save Fields')}
            </Button>
          </DialogOverlayActions>
        }
      >
        <FieldChipSelector
          handleSelection={(chips, lockedChips) => {
            setCurrentChips(chips)
            setCurrentLockedChips(lockedChips)
          }}
          chipData={[
            {
              title: t('edit-index.chips.event-fields.title', 'Event Fields'),
              data: filteredFieldChipData,
              key: 'eventFields',
            },
          ]}
          fieldData={selectedFields}
          unlinkingStates={unlinkingStates}
          allowSelectingMultiple
          showSearch
          lockSharedFields
        />
        {disabled && (
          <p>
            <NoticeWarning color={colors.yellow10} />
            {message}
          </p>
        )}
      </DialogOverlay>
    </>
  )
}

export default EventFieldsSelector
